import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  Fuel,
  DollarSign,
  TrendingUp,
  Gauge,
  Plus,
} from 'lucide-react';

interface FuelEntry {
  id: string;
  vehicle_name: string;
  date: string;
  gallons: number;
  cost_per_gallon: number;
  total_cost: number;
  odometer: number;
  station: string;
}

interface FuelResponse {
  entries: FuelEntry[];
  metrics: {
    total_gallons: number;
    total_cost: number;
    avg_cost_per_gallon: number;
    avg_mpg: number;
  };
}

export default function FuelPage() {
  const { data, isLoading } = useApi<FuelResponse>('/api/fuel');
  const [showNew, setShowNew] = useState(false);

  const metrics = data?.metrics ?? { total_gallons: 0, total_cost: 0, avg_cost_per_gallon: 0, avg_mpg: 0 };
  const entries = data?.entries ?? [];

  const metricCards = [
    { label: 'Total Gallons', value: metrics.total_gallons.toLocaleString(), icon: Fuel, color: 'text-emerald-400' },
    { label: 'Total Cost', value: `$${metrics.total_cost.toLocaleString()}`, icon: DollarSign, color: 'text-emerald-400' },
    { label: 'Avg Cost/Gallon', value: `$${metrics.avg_cost_per_gallon.toFixed(2)}`, icon: TrendingUp, color: 'text-amber-400' },
    { label: 'Avg MPG', value: metrics.avg_mpg.toFixed(1), icon: Gauge, color: 'text-blue-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Fuel className="h-6 w-6 text-emerald-500" />
          <h1 className="text-2xl font-bold text-zinc-100">Fuel Management</h1>
        </div>
        <button
          onClick={() => setShowNew(!showNew)}
          className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Fuel Entry
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metricCards.map((m) => (
          <div key={m.label} className="rounded-xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">{m.label}</span>
              <m.icon className={`h-5 w-5 ${m.color}`} />
            </div>
            <p className="mt-2 text-2xl font-semibold text-zinc-100">{m.value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-zinc-400">
                <th className="px-4 py-3 font-medium">Vehicle</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Gallons</th>
                <th className="px-4 py-3 font-medium">Cost/Gallon</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Odometer</th>
                <th className="px-4 py-3 font-medium">Station</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Loading fuel entries...</td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">No fuel entries found</td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-zinc-800/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-zinc-100">{entry.vehicle_name}</td>
                    <td className="px-4 py-3 text-zinc-400">{new Date(entry.date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-zinc-300">{entry.gallons.toFixed(2)}</td>
                    <td className="px-4 py-3 text-zinc-300">${entry.cost_per_gallon.toFixed(2)}</td>
                    <td className="px-4 py-3 text-zinc-300">${entry.total_cost.toFixed(2)}</td>
                    <td className="px-4 py-3 text-zinc-300">{entry.odometer.toLocaleString()}</td>
                    <td className="px-4 py-3 text-zinc-400">{entry.station}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
